/**
 * TransactionHistory
 * Tabbed transaction table with pagination for income and payout history.
 * Shows inline detail view for income transactions.
 */

import { ArrowLeft, Download, ExternalLink, LifeBuoy, MoreVertical, Search } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { useRelatedTransactions, useTransactionDetail, useTransactions } from "../hooks/useEarnings"
import type { Transaction, TransactionsResponse } from "../services/earnings.service"
import { getPayoutReceipt, refundTransaction } from "../services/earnings.service"
import { RefundDialog } from "./RefundDialog"

type Tab = "income" | "payout"

const SKELETON_ROWS = 5

export function TransactionHistory() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("income")
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [selectedRefundTransaction, setSelectedRefundTransaction] = useState<Transaction | null>(
    null,
  )

  const { data: transactions, isLoading } = useTransactions(
    activeTab,
    page,
    20,
    activeTab === "income" ? search : undefined,
  )
  const {
    data: selectedTransaction,
    isLoading: isLoadingDetail,
    refetch: refetchTransaction,
  } = useTransactionDetail(selectedTransactionId ?? "")
  const { data: relatedTransactions, refetch: refetchRelated } = useRelatedTransactions(
    selectedTransactionId ?? "",
  )

  const totalPages = transactions ? Math.ceil(transactions.total / transactions.limit) : 0

  const handleBackToList = () => {
    setSelectedTransactionId(null)
  }

  const handleSelectRow = (tx: Transaction) => {
    if (activeTab === "income") {
      setSelectedTransactionId(tx.id)
    }
  }

  const handleRefundSuccess = () => {
    refetchTransaction()
    refetchRelated()
  }

  const handleRefundConfirm = async (transactionId: string) => {
    await refundTransaction(transactionId)
  }

  const handleViewDetail = (tx: Transaction) => {
    setSelectedTransactionId(tx.id)
  }

  const handleViewProfile = (tx: Transaction) => {
    if (tx.buyerId) {
      navigate(`/profile/${tx.buyerId}`)
    } else {
      toast.error("User profile not available for this transaction")
    }
  }

  const handleRefundPayment = (tx: Transaction) => {
    setSelectedRefundTransaction(tx)
    setRefundDialogOpen(true)
  }

  const handleRemoveFromRitual = (tx: Transaction) => {
    console.log("Remove from ritual:", tx.id)
  }

  // Show inline detail view if a transaction is selected
  if (selectedTransactionId) {
    if (isLoadingDetail || !selectedTransaction) {
      return <TransactionDetailSkeleton />
    }

    return (
      <TransactionDetailView
        transaction={selectedTransaction}
        relatedTransactions={relatedTransactions ?? []}
        onBack={handleBackToList}
        onViewDetail={handleViewDetail}
        onViewProfile={handleViewProfile}
        onRefundPayment={handleRefundPayment}
        onRemoveFromRitual={handleRemoveFromRitual}
      />
    )
  }

  return (
    <div>
      <div className="flex items-center gap-3.75">
        <p className="uppercase text-[15px] leading-[25px] font-semibold">Ritual History</p>
        <div className="bg-[#F2F0EB1A] w-[1px] h-[30px]" />
        {(["income", "payout"] as Tab[]).map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setActiveTab(tab)
              setPage(1)
              setSelectedTransactionId(null)
            }}
            className={cn(
              "capitalize text-sm cursor-pointer",
              activeTab === tab ? "font-semibold text-primary" : "text-body hover:text-primary",
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "income" && (
        <div className="relative mt-3.75 mb-1.25">
          <Search className="absolute left-3.75 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search ritual or session"
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9 py-3 h-[50px] bg-[#F2F0EB0D] text-[#F2F0EB]! focus-visible:outline-0 focus-visible:ring-0"
          />
        </div>
      )}

      {isLoading ? (
        <TransactionTableSkeleton />
      ) : !transactions || transactions.rows.length === 0 ? (
        <EmptyState />
      ) : (
        <TransactionsTable
          rows={transactions.rows}
          activeTab={activeTab}
          onSelectRow={activeTab === "income" ? handleSelectRow : undefined}
        />
      )}

      {transactions && transactions.total > transactions.limit && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      <RefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        transaction={selectedRefundTransaction}
        onConfirm={handleRefundConfirm}
        onSuccess={handleRefundSuccess}
      />
    </div>
  )
}

function EmptyState() {
  return <div className="flex justify-center py-8 text-body text-sm">No transactions found</div>
}

function TransactionTableSkeleton() {
  return (
    <div className="space-y-0">
      <div className="flex gap-4 py-3.75">
        <Skeleton className="h-[14px] w-[60px]" />
        <Skeleton className="h-[14px] w-[80px]" />
        <Skeleton className="h-[14px] w-[60px]" />
        <Skeleton className="h-[14px] w-[50px]" />
        <Skeleton className="h-[14px] w-[70px]" />
      </div>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} className="flex gap-4 py-3.75 border-t border-[#F2F0EB26]">
          <Skeleton className="h-[14px] w-[60px]" />
          <Skeleton className="h-[14px] w-[80px]" />
          <Skeleton className="h-[14px] w-[60px]" />
          <Skeleton className="h-[14px] w-[50px]" />
          <Skeleton className="h-[14px] w-[70px]" />
        </div>
      ))}
    </div>
  )
}

function TransactionsTable({
  rows,
  activeTab,
  onSelectRow,
}: {
  rows: TransactionsResponse["rows"]
  activeTab: Tab
  onSelectRow?: (tx: Transaction) => void
}) {
  return (
    <Table className="text-[13px]">
      <TableHeader>
        <TableRow className="border-[#F2F0EB26] hover:bg-transparent">
          {activeTab === "payout" ? <PayoutTableHead /> : <IncomeTableHead />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(tx => (
          <TableRow
            key={tx.id}
            className={cn("border-[#F2F0EB26]", onSelectRow && "cursor-pointer")}
            onClick={() => onSelectRow?.(tx)}
          >
            {activeTab === "payout" ? <PayoutRow tx={tx} /> : <IncomeRow tx={tx} />}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function IncomeTableHead() {
  return (
    <>
      <TableHead className="text-[#FFFFFFB3] font-normal py-3.75 px-0">Title</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[175px] py-3.75 px-0">Status</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[151px] py-3.75 px-0">Date</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[74px] py-3.75 px-0">Sales</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[55px] py-3.75 px-0 text-end">
        Amount
      </TableHead>
    </>
  )
}

function PayoutTableHead() {
  return (
    <>
      <TableHead className="text-[#FFFFFFB3] font-normal py-3.75 px-0">Date</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[144px] py-3.75 px-0">Status</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[93px] py-3.75 px-0">Earned</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[85px] py-3.75 px-0">Fee</TableHead>
      <TableHead className="text-[#FFFFFFB3] font-normal w-[60px] py-3.75 px-0">Sent</TableHead>
      <TableHead className="w-[40px] py-3.75 px-0 text-end"></TableHead>
    </>
  )
}

function IncomeRow({ tx }: { tx: TransactionsResponse["rows"][number] }) {
  return (
    <>
      <TableCell
        className="text-primary truncate py-3.75 px-0 capitalize"
        title={tx.title ?? tx.description}
      >
        {tx.title ?? tx.description}
      </TableCell>
      <TableCell className="py-3.75 px-0 text-primary">
        <StatusBadge status={tx.status} />
      </TableCell>
      <TableCell className="text-primary py-3.75 px-0">{formatDate(tx.date)}</TableCell>
      <TableCell className="text-primary py-3.75 px-0">{tx.sales ?? 0}</TableCell>
      <TableCell className="font-semibold text-primary py-3.75 px-0 text-end">
        {formatCurrency(tx.amount ?? tx.earned)}
      </TableCell>
    </>
  )
}

function PayoutRow({ tx }: { tx: TransactionsResponse["rows"][number] }) {
  const handleViewOnStripe = () => {
    if (!tx.stripePayoutId) return

    const isTest = import.meta.env.MODE === "development"
    const baseUrl = isTest ? "https://dashboard.stripe.com/test" : "https://dashboard.stripe.com"
    window.open(`${baseUrl}/payouts/${tx.stripePayoutId}`, "_blank")
  }

  const handleDownloadReceipt = async () => {
    if (!tx.stripePayoutId) return

    try {
      const payout = await getPayoutReceipt(tx.stripePayoutId)
      const blob = new Blob([JSON.stringify(payout, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `payout-receipt-${tx.stripePayoutId}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error("Failed to download receipt")
    }
  }

  const handleContactSupport = () => {
    const subject = encodeURIComponent(`Payout Inquiry - ${tx.id}`)
    const body = encodeURIComponent(
      `Hi,\n\nI have a question about my payout:\n\nPayout ID: ${tx.id}\nAmount: ${formatCurrency(tx.sent)}\nDate: ${formatDate(tx.date)}\nStatus: ${tx.status}\n\nPlease help me with this issue.`,
    )
    window.location.href = `mailto:support@rituality.com?subject=${subject}&body=${body}`
  }

  return (
    <>
      <TableCell className="text-primary py-3.75 px-0">{formatDate(tx.date)}</TableCell>
      <TableCell className="py-3.75 px-0 text-primary">
        <StatusBadge status={tx.status} />
      </TableCell>
      <TableCell className="text-primary py-3.75 px-0">{formatCurrency(tx.earned)}</TableCell>
      <TableCell className="text-primary py-3.75 px-0">{formatCurrency(tx.fee)}</TableCell>
      <TableCell className="font-semibold text-primary py-3.75 px-0">
        {formatCurrency(tx.sent)}
      </TableCell>
      <TableCell className="py-3.75 px-0 text-end" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hover:text-primary transition-colors">
              <MoreVertical className="size-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[180px]">
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              onClick={handleDownloadReceipt}
              disabled={!tx.stripePayoutId}
            >
              <Download className="size-4 text-muted-foreground" />
              <span>Download Receipt</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              onClick={handleViewOnStripe}
              disabled={!tx.stripePayoutId}
            >
              <ExternalLink className="size-4 text-muted-foreground" />
              <span>View on Stripe</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer gap-2" onClick={handleContactSupport}>
              <LifeBuoy className="size-4 text-muted-foreground" />
              <span>Contact Support</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </>
  )
}

function StatusBadge({ status }: { status: string }) {
  const normalized = status.toUpperCase()

  const statusLabels: Record<string, string> = {
    COMPLETED: "Completed",
    PENDING: "Pending",
    REJECTED: "Rejected",
    PAID: "Paid",
    QUEUED: "Queued",
    SUBMITTED: "Submitted",
    PENDING_STRIPE_BALANCE: "Pending",
    FAILED: "Failed",
    CANCELED: "Canceled",
    REQUIRES_REVIEW: "Review",
  }

  const formatLabels: Record<string, string> = {
    IN_PERSON: "In Person",
    VIRTUAL: "Virtual",
    HYBRID: "Hybrid",
    VIRTUAL_COURSE: "Live Online",
    SELF_GUIDED: "Self Guided",
    SELF_GUIDED_COURSE: "Self Guided Course",
  }

  if (formatLabels[normalized]) {
    return <span className={cn("text-[13px]", "text-[#7C7D62]")}>{formatLabels[normalized]}</span>
  }

  const color =
    normalized === "COMPLETED" || normalized === "PAID"
      ? "text-[#7C7D62]"
      : normalized === "PENDING" ||
          normalized === "QUEUED" ||
          normalized === "SUBMITTED" ||
          normalized === "PENDING_STRIPE_BALANCE"
        ? "text-[#6A7E8C]"
        : normalized === "REJECTED" ||
            normalized === "FAILED" ||
            normalized === "CANCELED" ||
            normalized === "REQUIRES_REVIEW"
          ? "text-[#C86757]"
          : "text-muted-foreground"

  return (
    <span className={cn("text-[13px]", color)}>
      {statusLabels[normalized] ?? status.toLowerCase()}
    </span>
  )
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  return (
    <div className="flex justify-center gap-2 mt-4">
      <Button
        variant="ghost"
        size="sm"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Previous
      </Button>
      <span className="text-[13px] text-body py-2">
        Page {currentPage} of {totalPages}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  )
}

function formatCurrency(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount)
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

// ============================================================================
// INLINE DETAIL VIEW COMPONENTS
// ============================================================================

interface TransactionDetailViewProps {
  transaction: Transaction
  relatedTransactions: Transaction[]
  onBack: () => void
  onViewDetail: (tx: Transaction) => void
  onViewProfile: (tx: Transaction) => void
  onRefundPayment: (tx: Transaction) => void
  onRemoveFromRitual: (tx: Transaction) => void
}

function TransactionDetailView({
  transaction,
  relatedTransactions,
  onBack,
  onViewDetail,
  onViewProfile,
  onRefundPayment,
  onRemoveFromRitual,
}: TransactionDetailViewProps) {
  const [search, setSearch] = useState("")
  const isIncome = transaction.type === "income"
  const totalEarning = isIncome ? (transaction.amount ?? transaction.earned) : transaction.sent
  const salesCount = isIncome ? (transaction.sales ?? 0) : 0

  const filteredTransactions = relatedTransactions.filter(
    tx =>
      (tx.buyerName ?? tx.title ?? tx.description).toLowerCase().includes(search.toLowerCase()) ||
      tx.status.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <div className="space-y-2.5">
      {/* Back Button */}
      <Button
        variant="ghost"
        type="button"
        onClick={onBack}
        className="flex items-center gap-3.75 text-[15px] pl-0 h-[30px] font-semibold leading-6.25 text-primary hover:text-primary transition-colors"
      >
        <ArrowLeft className="size-5" />
        <span>Back to History</span>
      </Button>

      {/* Header Section */}
      <div className="space-y-2.5 pt-3.75 pb-2.5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-primary capitalize">
            {transaction.title ?? transaction.description}
          </h1>
          {/* Total Earning */}
          <div className="flex items-center gap-1 text-[15px] text-primary leading-5">
            <span>Total Earning:</span>
            <span className="font-semibold">
              {formatCurrency(totalEarning, transaction.currency)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <span className="text-[13px] text-primary leading-5 capitalize">
            <StatusBadge status={transaction?.format ?? ""} />
          </span>
          {/* Status  */}
          <span className="text-[13px] text-primary leading-5 capitalize">
            <StatusBadge status={transaction.status} />
          </span>
          {/* Sales Count */}
          <div>
            {isIncome && salesCount > 0 && (
              <span className="text-[13px] leading-5 text-primary">
                {salesCount} {salesCount === 1 ? "sale" : "sales"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Related Transactions Table */}
      <div>
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3.75 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name or username..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 py-3 h-[50px] bg-[#F2F0EB0D] text-[#F2F0EB]! focus-visible:outline-0 focus-visible:ring-0"
          />
        </div>

        {filteredTransactions.length > 0 ? (
          <RelatedTransactionsTable
            transactions={filteredTransactions}
            onViewDetail={onViewDetail}
            onViewProfile={onViewProfile}
            onRefundPayment={onRefundPayment}
            onRemoveFromRitual={onRemoveFromRitual}
          />
        ) : (
          <div className="flex justify-center py-8 text-body text-sm">
            {search ? "No transactions match your search" : "No related transactions found"}
          </div>
        )}
      </div>
    </div>
  )
}

interface RelatedTransactionsTableProps {
  transactions: Transaction[]
  onViewDetail: (tx: Transaction) => void
  onViewProfile: (tx: Transaction) => void
  onRefundPayment: (tx: Transaction) => void
  onRemoveFromRitual: (tx: Transaction) => void
}

function RelatedTransactionsTable({
  transactions,
  onViewDetail,
  onViewProfile,
  onRefundPayment,
  onRemoveFromRitual,
}: RelatedTransactionsTableProps) {
  return (
    <Table className="text-[13px]">
      <TableHeader>
        <TableRow className="border-[#F2F0EB26] hover:bg-transparent">
          <TableHead className="text-[#FFFFFFB3] font-normal py-3.75 px-0">Name</TableHead>
          <TableHead className="text-[#FFFFFFB3] font-normal w-[175px] py-3.75 px-0">
            Status
          </TableHead>
          <TableHead className="text-[#FFFFFFB3] font-normal w-[151px] py-3.75 px-0">
            Date
          </TableHead>
          <TableHead className="text-[#FFFFFFB3] font-normal w-[100px] py-3.75 px-0">
            Amount
          </TableHead>
          <TableHead className="w-[40px] py-3.75 px-0"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map(tx => (
          <TableRow key={tx.id} className="border-[#F2F0EB26]">
            <TableCell
              className="text-primary truncate py-3.75 px-0 capitalize"
              title={tx.buyerName ?? tx.title ?? tx.description}
            >
              {tx.buyerName ?? tx.title ?? tx.description}
            </TableCell>
            <TableCell className="py-3.75 px-0 text-primary">
              <StatusBadge status={tx.status} />
            </TableCell>
            <TableCell className="text-primary py-3.75 px-0">{formatDate(tx.date)}</TableCell>
            <TableCell className="font-semibold text-primary py-3.75 px-0">
              {formatCurrency(tx.amount ?? tx.earned)}
            </TableCell>
            <TableCell className="py-3.75 px-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hover:text-primary transition-colors">
                    <MoreVertical className="size-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onViewDetail(tx)}>View Detail</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onViewProfile(tx)}>
                    View Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRefundPayment(tx)}>
                    Refund Payment
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onRemoveFromRitual(tx)}
                  >
                    Remove from Ritual
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function TransactionDetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Back Button Skeleton */}
      <Skeleton className="h-[36px] w-[140px]" />

      {/* Header Skeleton */}
      <div className="space-y-4">
        <Skeleton className="h-[28px] w-[300px]" />
        <div className="flex items-center gap-6">
          <Skeleton className="h-[24px] w-[150px]" />
          <Skeleton className="h-[24px] w-[120px]" />
          <Skeleton className="h-[24px] w-[80px]" />
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="space-y-3">
        <Skeleton className="h-[20px] w-[200px]" />
        <div className="flex gap-4 py-3.75">
          <Skeleton className="h-[14px] w-[60px]" />
          <Skeleton className="h-[14px] w-[80px]" />
          <Skeleton className="h-[14px] w-[60px]" />
          <Skeleton className="h-[14px] w-[70px]" />
          <Skeleton className="h-[14px] w-[40px]" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-4 py-3.75 border-t border-[#F2F0EB26]">
            <Skeleton className="h-[14px] w-[60px]" />
            <Skeleton className="h-[14px] w-[80px]" />
            <Skeleton className="h-[14px] w-[60px]" />
            <Skeleton className="h-[14px] w-[70px]" />
            <Skeleton className="h-[14px] w-[40px]" />
          </div>
        ))}
      </div>
    </div>
  )
}
